// Date helpers to avoid timezone bugs.
//
// The app stores dates as plain yyyy-MM-dd strings (no time, no timezone).
// Using `new Date("2026-05-22").toISOString().split('T')[0]` is broken:
//  - `new Date("2026-05-22")` parses as UTC midnight.
//  - For users ahead of UTC (Ireland in summer is UTC+1), the local date
//    of that moment could be the previous or same day depending on the hour.
//  - `.toISOString()` converts back to UTC, which can shift the date string.
//
// The helpers below always operate in LOCAL time (Europe/Dublin on the user's
// machine), so a stored "2026-05-22" is always shown as Friday 22 May 2026
// regardless of the user's clock.

/**
 * Convert a Date object to a yyyy-MM-dd string using LOCAL date parts.
 * Never goes through UTC, so it will not shift the date.
 */
export function toLocalDateKey(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Parse a yyyy-MM-dd string (or a Date) into a LOCAL-midnight Date object.
 * Safe to call getDay / getDate on the result.
 */
export function parseLocalDate(value) {
  if (!value) return null;
  if (value instanceof Date) return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  if (typeof value !== 'string') return null;
  // Accept yyyy-MM-dd or yyyy-MM-ddTHH:mm:ss...
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10) - 1;
  const day = parseInt(m[3], 10);
  return new Date(year, month, day);
}

/**
 * Today's date as yyyy-MM-dd in local time.
 */
export function todayKey() {
  return toLocalDateKey(new Date());
}

/**
 * Day of week for a date string, using local time.
 * Returns 0 (Sun) through 6 (Sat).
 */
export function dayOfWeek(value) {
  const d = parseLocalDate(value);
  return d ? d.getDay() : null;
}

/**
 * Compare two date values as dates only (ignoring time).
 * Returns negative / zero / positive like a comparator.
 */
export function compareDates(a, b) {
  const ka = toLocalDateKey(a);
  const kb = toLocalDateKey(b);
  if (ka < kb) return -1;
  if (ka > kb) return 1;
  return 0;
}

/**
 * Check whether a date (Date or string) is today in local time.
 */
export function isToday(value) {
  return toLocalDateKey(value) === todayKey();
}

/**
 * Add N days to a date (Date or string) and return a new Date at local midnight.
 */
export function addDays(value, n) {
  const d = parseLocalDate(value) || new Date();
  const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  copy.setDate(copy.getDate() + n);
  return copy;
}

/**
 * Format a date as a human-readable Irish date, e.g. "Fri 22 May 2026".
 * Always uses local time.
 */
export function formatIrishDate(value, opts) {
  const d = parseLocalDate(value);
  if (!d) return '';
  return d.toLocaleDateString('en-IE', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(opts || {}),
  });
}
